import {Request} from "express";
import {Headers} from "node-fetch";

import {isNumber} from "@pg-mono/nodash";
import {IRequestMeta} from "@pg-mono/request";

import {IABTestConfiguration, IVariantConfigParams, VariantConfig, VariantWeights} from "../types/IABTestConfiguration";
import {buildABTestCookie} from "./ab_testing_utils";
import {getAbTestVariantFromQuery} from "./get_ab_test_variant_from_query";
import {getWeightedRandomIndex} from "./get_weighted_random_index";

const WEIGHT_SUM_TOLERANCE = 0.0001;

/**
 * Parse cookies to find Google Optimize AB test variant or - if cookie was no set - define new AB test variant.
 * Set newly defined AB test variant in res.locals in order to populate this value in the request flow.
 * @param abTestConfig {IABTestConfiguration} AB test configuration object
 * @param domain {string} domain value will be set on AB test cookie
 * @param req {Request} request object
 * @param meta {IRequestMeta} request meta object
 * @param params {IVariantConfigParams} parameters for variant config (e.g. isAuthenticated)
 * @returns {number} Google Optimize AB test variant
 */
export const specifyABTestVariant = (
    {id, expires, variants, sampleRate, variantConfig}: IABTestConfiguration,
    domain: string,
    req?: Partial<Request>,
    meta?: Partial<IRequestMeta>,
    params?: IVariantConfigParams
): number => {
    // QA wants to always test base variant in automated tests
    const isE2eTesting = req?.headers?.["x-request-source"] === "e2e";

    const abTestVariantFromCookie = getABTestVariantFromCookies(id, req);

    // Resolve and validate weights based on config and params
    let weights: number[] | undefined;
    if (variantConfig && params) {
        weights = resolveVariantWeights(variantConfig, variants, params);
        validateVariantWeights(weights, variants);
    }

    let abTestVariant = isE2eTesting ? 0 : drawABTestVariant(variants, weights);

    if (sampleRate) {
        const isTestGroup = !getWeightedRandomIndex([sampleRate, 1 - sampleRate]);

        if (!isTestGroup) {
            /*
             * -1 indicates that the user is not part of the test group.
             * -1 is identical to 0 (base variant) in terms of rendered HTML, but this value should not generate GTM event.
             * We need to store this value in the cookie to avoid group changes on every request.
             */
            abTestVariant = -1;
        }
    }

    /*
     * With use of ab_test_experiment and ab_test_variant query params we can override AB test variant.
     * Just add above params to the URL - if valid values will override AB test variant from cookie.
     */
    const abTestVariantFromQuery = getAbTestVariantFromQuery(id, variants, Boolean(sampleRate), req);

    if ((!abTestVariantFromCookie || isNumber(abTestVariantFromQuery)) && meta) {
        const headers = new Headers({"set-cookie": buildABTestCookie(id, abTestVariantFromQuery ?? abTestVariant, expires, domain)});

        meta?.serverResponseHeaders?.appendHeaders(headers);
    }

    if (abTestVariantFromQuery) {
        return abTestVariantFromQuery;
    }

    return abTestVariantFromCookie ? parseInt(abTestVariantFromCookie) : abTestVariant;
};

/**
 * Parse cookies to find Google Optimize AB test variant
 * @param abTestId {string} id of the Google Optimize AB test
 * @param req {Request} request object
 * @returns {number | null} Google Optimize AB test variant
 */
export const getABTestVariantFromCookies = (abTestId: string, req?: Partial<Request>): string | null => {
    if (req && req.cookies) {
        return req.cookies[abTestId] ?? null;
    }

    return null;
};

/**
 * Resolve variant weights from config and convert to array
 * @param variantConfig {VariantConfig} variant configuration (object or function)
 * @param variants {number} number of variants
 * @param params {IVariantConfigParams} parameters for function-based config
 * @returns {number[]} array of weights
 */
const resolveVariantWeights = (variantConfig: VariantConfig, variants: number, params: IVariantConfigParams): number[] => {
    // Resolve config - if function, call it; if object, use directly
    const weights: VariantWeights = typeof variantConfig === "function" ? variantConfig(params) : variantConfig;

    // Convert object {0: 0.8, 1: 0.2} to array [0.8, 0.2]
    const weightsArray: number[] = [];
    for (let i = 0; i <= variants; i++) {
        if (weights[i] === undefined) {
            throw new Error(`variantConfig missing weight for variant ${i}. Expected all variants from 0 to ${variants}`);
        }
        weightsArray.push(weights[i]);
    }

    return weightsArray;
};

/**
 * Validate variant weights
 * @param weights {number[]} array of weights
 * @param variants {number} number of variants
 */
const validateVariantWeights = (weights: number[], variants: number): void => {
    if (weights.length !== variants + 1) {
        throw new Error(`variantConfig must have weights for all variants. Expected ${variants + 1} variants (0-${variants}), got ${weights.length}`);
    }

    if (weights.some((w) => w < 0 || w > 1)) {
        throw new Error("All weights must be between 0 and 1");
    }

    const sum = weights.reduce((acc, w) => acc + w, 0);
    if (Math.abs(sum - 1) > WEIGHT_SUM_TOLERANCE) {
        throw new Error(`Weights must sum to 1.0. Current sum: ${sum}`);
    }
};

/**
 * Draw new AB test variant.
 * @param variants {number} total Google Optimize variants including original variant
 * @param weights {number[]} optional array of weights for weighted random selection
 * @returns {number} new Google Optimize AB test variant
 */
const drawABTestVariant = (variants: number, weights?: number[]): number => {
    if (weights && weights.length === variants + 1) {
        return getWeightedRandomIndex(weights);
    }
    return Math.floor(Math.random() * (variants + 1));
};
import {Request} from "express";
import {Headers} from "node-fetch";

import {isNumber} from "@pg-mono/nodash";
import {IRequestMeta} from "@pg-mono/request";

import {IABTestConfiguration, IVariantConfigParams, VariantConfig, VariantWeights} from "../types/IABTestConfiguration";
import {buildABTestCookie} from "./ab_testing_utils";
import {getAbTestVariantFromQuery} from "./get_ab_test_variant_from_query";
import {getWeightedRandomIndex} from "./get_weighted_random_index";

const WEIGHT_SUM_TOLERANCE = 0.0001;

/**
 * Parse cookies to find Google Optimize AB test variant or - if cookie was no set - define new AB test variant.
 * Set newly defined AB test variant in res.locals in order to populate this value in the request flow.
 * @param abTestConfig {IABTestConfiguration} AB test configuration object
 * @param domain {string} domain value will be set on AB test cookie
 * @param req {Request} request object
 * @param meta {IRequestMeta} request meta object
 * @param params {IVariantConfigParams} parameters for variant config (e.g. isAuthenticated)
 * @returns {number} Google Optimize AB test variant
 */
export const specifyABTestVariant = (
    {id, expires, variants, sampleRate, variantConfig}: IABTestConfiguration,
    domain: string,
    req?: Partial<Request>,
    meta?: Partial<IRequestMeta>,
    params?: IVariantConfigParams
): number => {
    // QA wants to always test base variant in automated tests
    const isE2eTesting = req?.headers?.["x-request-source"] === "e2e";

    const abTestVariantFromCookie = getABTestVariantFromCookies(id, req);

    // Resolve and validate weights based on config and params
    let weights: number[] | undefined;
    if (variantConfig && params) {
        weights = resolveVariantWeights(variantConfig, variants, params);
        validateVariantWeights(weights, variants);
    }

    let abTestVariant = isE2eTesting ? 0 : drawABTestVariant(variants, weights);

    if (sampleRate) {
        const isTestGroup = !getWeightedRandomIndex([sampleRate, 1 - sampleRate]);

        if (!isTestGroup) {
            /*
             * -1 indicates that the user is not part of the test group.
             * -1 is identical to 0 (base variant) in terms of rendered HTML, but this value should not generate GTM event.
             * We need to store this value in the cookie to avoid group changes on every request.
             */
            abTestVariant = -1;
        }
    }

    /*
     * With use of ab_test_experiment and ab_test_variant query params we can override AB test variant.
     * Just add above params to the URL - if valid values will override AB test variant from cookie.
     */
    const abTestVariantFromQuery = getAbTestVariantFromQuery(id, variants, Boolean(sampleRate), req);

    if ((!abTestVariantFromCookie || isNumber(abTestVariantFromQuery)) && meta) {
        const headers = new Headers({"set-cookie": buildABTestCookie(id, abTestVariantFromQuery ?? abTestVariant, expires, domain)});

        meta?.serverResponseHeaders?.appendHeaders(headers);
    }

    if (abTestVariantFromQuery) {
        return abTestVariantFromQuery;
    }

    return abTestVariantFromCookie ? parseInt(abTestVariantFromCookie) : abTestVariant;
};

/**
 * Parse cookies to find Google Optimize AB test variant
 * @param abTestId {string} id of the Google Optimize AB test
 * @param req {Request} request object
 * @returns {number | null} Google Optimize AB test variant
 */
export const getABTestVariantFromCookies = (abTestId: string, req?: Partial<Request>): string | null => {
    if (req && req.cookies) {
        return req.cookies[abTestId] ?? null;
    }

    return null;
};

/**
 * Resolve variant weights from config and convert to array
 * @param variantConfig {VariantConfig} variant configuration (object or function)
 * @param variants {number} number of variants
 * @param params {IVariantConfigParams} parameters for function-based config
 * @returns {number[]} array of weights
 */
const resolveVariantWeights = (variantConfig: VariantConfig, variants: number, params: IVariantConfigParams): number[] => {
    // Resolve config - if function, call it; if object, use directly
    const weights: VariantWeights = typeof variantConfig === "function" ? variantConfig(params) : variantConfig;

    // Convert object {0: 0.8, 1: 0.2} to array [0.8, 0.2]
    const weightsArray: number[] = [];
    for (let i = 0; i <= variants; i++) {
        if (weights[i] === undefined) {
            throw new Error(`variantConfig missing weight for variant ${i}. Expected all variants from 0 to ${variants}`);
        }
        weightsArray.push(weights[i]);
    }

    return weightsArray;
};

/**
 * Validate variant weights
 * @param weights {number[]} array of weights
 * @param variants {number} number of variants
 */
const validateVariantWeights = (weights: number[], variants: number): void => {
    if (weights.length !== variants + 1) {
        throw new Error(`variantConfig must have weights for all variants. Expected ${variants + 1} variants (0-${variants}), got ${weights.length}`);
    }

    if (weights.some((w) => w < 0 || w > 1)) {
        throw new Error("All weights must be between 0 and 1");
    }

    const sum = weights.reduce((acc, w) => acc + w, 0);
    if (Math.abs(sum - 1) > WEIGHT_SUM_TOLERANCE) {
        throw new Error(`Weights must sum to 1.0. Current sum: ${sum}`);
    }
};

/**
 * Draw new AB test variant.
 * @param variants {number} total Google Optimize variants including original variant
 * @param weights {number[]} optional array of weights for weighted random selection
 * @returns {number} new Google Optimize AB test variant
 */
const drawABTestVariant = (variants: number, weights?: number[]): number => {
    if (weights && weights.length === variants + 1) {
        return getWeightedRandomIndex(weights);
    }
    return Math.floor(Math.random() * (variants + 1));
};
